import React from 'react';
import { Select } from '@mantine/core';
import { BaseFieldProps } from './types';
import { BooleanMetadata, SelectMetadata } from 'src/data-capture/types';
import { useFlags } from 'src/feature-flags/FlagsHook';

const PINNED_TOP_VALUE_BY_FIELD: Record<string, string> = {
  nationality: 'British',
  uk_residency_status: 'UK_CITIZEN',
};

export function SelectField<T extends Record<string, unknown>>({
  fieldLabel,
  entityMetadata,
  disabled,
  rightValue,
  form,
  formFieldName,
  variant,
  fieldName,
  onChange,
}: BaseFieldProps<T>) {
  const { flagEnabledPinnedNationalityResidencyOptions } = useFlags();

  if (entityMetadata.type !== 'SELECT' && entityMetadata.type !== 'BOOLEAN') {
    return null;
  }

  const pinnedValue = flagEnabledPinnedNationalityResidencyOptions
    ? PINNED_TOP_VALUE_BY_FIELD[fieldName]
    : undefined;
  const options = generateOptionsFromMetadata(entityMetadata, pinnedValue);
  const inputProps = form.getInputProps(formFieldName);

  return (
    <Select
      id={formFieldName}
      label={fieldLabel}
      labelProps={{ variant: 'light' }}
      required={!entityMetadata.isOptional}
      data={options}
      searchable={options.length > 10}
      disabled={disabled}
      rightSection={rightValue}
      clearable
      key={form.key(formFieldName)}
      {...inputProps}
      variant={variant}
      onChange={value => onChange(value, fieldName)}
    />
  );
}

export const generateOptionsFromMetadata = (
  metadata: SelectMetadata | BooleanMetadata,
  pinnedValue?: string,
) => {
  return [
    ...metadata.options
      .map(option => ({
        value: option.value.toString(),
        label: option.label.toString(),
      }))
      .sort((a, b) => {
        if (metadata.type === 'BOOLEAN') {
          return a.value === 'true' ? -1 : 1;
        }

        if (pinnedValue) {
          if (a.value === pinnedValue) return -1;
          if (b.value === pinnedValue) return 1;
        }

        return a.label.localeCompare(b.label);
      }),
  ];
};
